import { useEffect, useRef, useState, type MouseEvent, type PointerEvent } from "react";
import { NavLink } from "react-router";
import { indiceEn, MANTENER_MS, posicionBurbuja, TOLERANCIA_PX } from "@/lib/barra";
import { cn } from "@/lib/cn";
import type { NombreIcono } from "@/lib/navegacion";
import { Icono } from "./Icono";

export interface ItemBarra {
  clave: string;
  etiqueta: string;
  icono: NombreIcono;
  activo: boolean;
  /** Con ruta, el botón es un link directo; sin ruta (sólo "Más"), un botón que abre su hoja (`aria-haspopup="dialog"`). */
  ruta?: string;
}

export interface BarraInferiorProps {
  items: ItemBarra[];
  /** Se llama al tocar un botón sin ruta, o al soltar la burbuja arrastrada sobre cualquier botón. */
  onElegir: (item: ItemBarra) => void;
}

interface Arrastre {
  /** Posición del dedo desde el borde izquierdo de la barra. */
  x: number;
  ancho: number;
  izquierda: number;
}

/**
 * Barra inferior del celular (Mati, 2026-11-03: como la de WhatsApp): píldora flotante translúcida con una burbuja
 * detrás del grupo activo, que se desliza al cambiar de pantalla (las pantallas del grupo van como pestañas arriba, en `AppShell`). Manteniendo apretado (`MANTENER_MS`), la
 * burbuja se levanta, sigue al dedo marcando el botón de abajo y, al soltar, va ahí (`onElegir`). Cuentas en `lib/barra.ts`.
 */
export function BarraInferior({ items, onElegir }: BarraInferiorProps) {
  const lista = useRef<HTMLUListElement>(null);
  const temporizador = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toque = useRef<{ x: number; y: number; id: number } | null>(null);
  const recienSoltado = useRef(false);
  const [arrastre, setArrastre] = useState<Arrastre | null>(null);

  const n = items.length;
  const iActivo = items.findIndex((i) => i.activo);
  const iMarcado = arrastre ? indiceEn(arrastre.x, arrastre.ancho, n) : iActivo;

  // Un golpecito (en los celulares que vibran) al levantar la burbuja y cada vez que cambia de botón.
  useEffect(() => {
    if (arrastre) navigator.vibrate?.(8);
  }, [arrastre !== null, iMarcado]);

  useEffect(() => () => cancelarEspera(), []);

  function cancelarEspera() {
    if (temporizador.current) clearTimeout(temporizador.current);
    temporizador.current = null;
  }

  function alApretar(e: PointerEvent<HTMLUListElement>) {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    toque.current = { x: e.clientX, y: e.clientY, id: e.pointerId };
    cancelarEspera();
    temporizador.current = setTimeout(() => {
      const el = lista.current;
      const t = toque.current;
      if (!el || !t) return;
      const r = el.getBoundingClientRect();
      try {
        el.setPointerCapture?.(t.id);
      } catch {
        // el puntero ya no existe: igual se arrastra con los eventos que lleguen
      }
      setArrastre({ x: t.x - r.left, ancho: r.width, izquierda: r.left });
    }, MANTENER_MS);
  }

  function alMover(e: PointerEvent<HTMLUListElement>) {
    if (arrastre) {
      setArrastre({ ...arrastre, x: e.clientX - arrastre.izquierda });
      return;
    }
    const t = toque.current;
    if (t && Math.hypot(e.clientX - t.x, e.clientY - t.y) > TOLERANCIA_PX) {
      cancelarEspera();
      toque.current = null;
    }
  }

  function alSoltar(e: PointerEvent<HTMLUListElement>) {
    cancelarEspera();
    toque.current = null;
    if (!arrastre) return;
    const item = items[indiceEn(e.clientX - arrastre.izquierda, arrastre.ancho, n)];
    setArrastre(null);
    // El navegador manda un click después de soltar: que no abra además lo que estaba debajo del dedo al empezar.
    recienSoltado.current = true;
    setTimeout(() => (recienSoltado.current = false), 400);
    if (item) onElegir(item);
  }

  function alCancelar() {
    cancelarEspera();
    toque.current = null;
    setArrastre(null);
  }

  function alClic(e: MouseEvent) {
    if (!recienSoltado.current) return;
    recienSoltado.current = false;
    e.preventDefault();
    e.stopPropagation();
  }

  const estiloBurbuja = arrastre
    ? { width: `${100 / n}%`, transform: `translateX(${posicionBurbuja(arrastre.x, arrastre.ancho, n)}px) scale(1.08)` }
    : { width: `${100 / n}%`, transform: `translateX(${Math.max(iActivo, 0) * 100}%)` };

  return (
    <nav
      aria-label="Secciones"
      className="fixed inset-x-3 bottom-[max(0.75rem,env(safe-area-inset-bottom))] z-20 rounded-full border border-white/70 bg-fondo/75 p-1 shadow-flotante backdrop-blur-xl md:hidden"
    >
      <ul
        ref={lista}
        data-arrastrando={arrastre ? "" : undefined}
        onPointerDown={alApretar}
        onPointerMove={alMover}
        onPointerUp={alSoltar}
        onPointerCancel={alCancelar}
        onClickCapture={alClic}
        onContextMenu={(e) => e.preventDefault()}
        className="relative flex touch-none select-none [-webkit-touch-callout:none]"
      >
        <li
          aria-hidden
          data-burbuja
          className={cn(
            "pointer-events-none absolute inset-y-0 left-0 rounded-full bg-primario-suave",
            arrastre ? "shadow-flotante" : "transition-transform duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)]",
            iActivo < 0 && !arrastre && "opacity-0",
          )}
          style={estiloBurbuja}
        />
        {items.map((item, i) => {
          const clase = cn(
            "relative z-[1] flex min-w-0 flex-1 flex-col items-center gap-0.5 rounded-full px-1 py-1.5 text-[0.6875rem] font-medium leading-tight transition-colors",
            i === iMarcado ? "text-primario" : "text-texto-suave",
          );
          const contenido = (
            <>
              <Icono nombre={item.icono} />
              <span aria-hidden="true" className="max-w-full truncate">
                {item.etiqueta}
              </span>
            </>
          );
          return (
            <li key={item.clave} className="flex min-w-0 flex-1">
              {item.ruta !== undefined ? (
                <NavLink to={item.ruta} end={item.ruta === "/"} className={clase} aria-label={item.etiqueta} draggable={false}>
                  {contenido}
                </NavLink>
              ) : (
                <button
                  type="button"
                  className={clase}
                  aria-haspopup="dialog"
                  aria-current={item.activo ? "true" : undefined}
                  aria-label={item.etiqueta}
                  onClick={() => onElegir(item)}
                >
                  {contenido}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
